import React, { useState, useEffect } from "react";
import {
  Button,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  Table,
} from "reactstrap";
import {

  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
} from "reactstrap";

import * as XLSX from "xlsx";
import classnames from "classnames";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Label as YLabel,
} from "recharts";
import axios from "axios";

/* -------- UTILS -------- */

const safeKey = (value) =>
  value.replace(/[^a-zA-Z0-9_]/g, "_");

const GOOD_COLORS = [
  "#2e7d32",
  "#388e3c",
  "#43a047",
  "#4caf50",
  "#66bb6a",
  "#81c784",
];

const BAD_COLORS = [
  "#c62828",
  "#d32f2f",
  "#e53935",
  "#f44336",
  "#ef5350",
  "#e57373",
];

const getGoodColor = (i) => GOOD_COLORS[i % GOOD_COLORS.length];
const getBadColor = (i) => BAD_COLORS[i % BAD_COLORS.length];

/* -------- COMPONENT -------- */

const DateRangeReport = ({
  selectedMachine,
  selectedPart,
  fromDate,
  toDate,
  qualityTab,
  setQualityTab,
}) => {
  const [rangeData, setRangeData] = useState([]);
  const [rangeParts, setRangeParts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedMachine && fromDate && toDate) {
      fetchDateRangeProduction();
    }
  }, [selectedMachine, selectedPart, fromDate, toDate]);

  const fetchDateRangeProduction = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(
        "https://api.hanoch.in/api/mcc-production-graph-daterange/",
        {
          params: {
            machine_id: selectedMachine,
            start_date: fromDate,
            end_date: toDate,
          },
        }
      );

      const parts = res.data?.parts || [];
      setRangeParts(parts);

      const dateMap = {};

      parts.forEach((part) => {
        if (selectedPart && part.part_number !== selectedPart) return;

        const key = safeKey(part.part_number);

        part.series.forEach((d) => {
          if (!dateMap[d.date]) {
            dateMap[d.date] = { date: d.date };
          }

          dateMap[d.date][`${key}_good`] = d.good || 0;
          dateMap[d.date][`${key}_bad`] = d.bad || 0;
        });
      });

      setRangeData(
        Object.values(dateMap).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        )
      );
    } catch (err) {
      console.error(err);
      setRangeData([]);
    } finally {
      setIsLoading(false);
    }
  };

  /* -------- EXCEL -------- */

  const exportTableToExcel = () => {
    const rows = [];

    rangeData.forEach((row) => {
      rangeParts
        .filter((p) => !selectedPart || p.part_number === selectedPart)
        .forEach((part) => {
          const key = safeKey(part.part_number);
          const good = row[`${key}_good`] || 0;
          const bad = row[`${key}_bad`] || 0;

          if (qualityTab === "GOOD" && good === 0) return;
          if (qualityTab === "BAD" && bad === 0) return;

          const record = {
            Date: row.date,
            Part: part.part_number,
          };

          if (qualityTab !== "BAD") record.Good = good;
          if (qualityTab !== "GOOD") record.Bad = bad;

          rows.push(record);
        });
    });

    if (!rows.length) return;

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Production");

    XLSX.writeFile(
      wb,
      `Production_${selectedMachine}_${fromDate}_to_${toDate}_${qualityTab}.xlsx`
    );
  };

  /* -------- UI -------- */
const totals = rangeData.reduce(
  (acc, row) => {
    rangeParts
      .filter(
        (p) => !selectedPart || p.part_number === selectedPart
      )
      .forEach((part) => {
        const key = safeKey(part.part_number);
        acc.good += row[`${key}_good`] || 0;
        acc.bad += row[`${key}_bad`] || 0;
      });

    return acc;
  },
  { good: 0, bad: 0 }
);

const totalGood = totals.good;
const totalBad = totals.bad;
const totalProduction = totalGood + totalBad;

  return (
    <>
    {!isLoading && (
  <Row className="mb-3">
    <Col md="4">
      <Card  className="text-center shadow border border-secondary"
            style={{ backgroundColor: "rgb(37, 43, 59);", color: "#252231" }}>
        <CardBody>
          <CardTitle tag="h6" className="text-success">
            Total Good
          </CardTitle>
          <h3 className="fw-bold">{totalGood}</h3>
        </CardBody>
      </Card>
    </Col>

    <Col md="4">
      <Card  className="text-center shadow border border-secondary"
            style={{ backgroundColor: "rgb(37, 43, 59);", color: "#252231" }}>
        <CardBody>
          <CardTitle tag="h6" className="text-danger">
            Total Bad
          </CardTitle>
          <h3 className="fw-bold">{totalBad}</h3>
        </CardBody>
      </Card>
    </Col>

    <Col md="4">
      <Card  className="text-center shadow border border-secondary"
            style={{ backgroundColor: "rgb(37, 43, 59);", color: "#252231" }}>
        <CardBody>
          <CardTitle tag="h6" className="text-primary">
            Total Production
          </CardTitle>
          <h3 className="fw-bold">{totalProduction}</h3>
        </CardBody>
      </Card>
    </Col>
  </Row>
)}

      <Nav pills className="justify-content-center mb-3">
        {["ALL", "GOOD", "BAD"].map((tab) => (
          <NavItem key={tab}>
            <NavLink
              className={classnames({ active: qualityTab === tab })}
              onClick={() => setQualityTab(tab)}
              style={{ cursor: "pointer" }}
            >
              {tab}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <div style={{ height: 340 }}>
        <ResponsiveContainer>
          <BarChart data={rangeData}
            style={{ background: "transparent" }}>
            <XAxis dataKey="date" />
            <YAxis>
              <YLabel value="Quantity" angle={-90} position="insideLeft" />
            </YAxis>

            <Tooltip />
            <Legend />

            {rangeParts
              .filter((p) => !selectedPart || p.part_number === selectedPart)
              .map((part, index) => {
                const key = safeKey(part.part_number);

                return (
                  <React.Fragment key={part.part_number}>
                    {(qualityTab === "ALL" || qualityTab === "GOOD") && (
                      <Bar
                        dataKey={`${key}_good`}
                        name={`${part.part_number} (Good)`}
                        fill={getGoodColor(index)}
                      />
                    )}

                    {(qualityTab === "ALL" || qualityTab === "BAD") && (
                      <Bar
                        dataKey={`${key}_bad`}
                        name={`${part.part_number} (Bad)`}
                        fill={getBadColor(index)}
                        opacity={0.9}
                        
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="d-flex justify-content-end mb-2">
        <Button
          color="success"
          size="sm"
          onClick={exportTableToExcel}
          disabled={!rangeData.length}
        >
          Export Excel
        </Button>
      </div>

      <Table bordered className="mt-4">
        <thead>
          <tr>
            <th>Date</th>
            <th>Part</th>
            {qualityTab !== "BAD" && <th>Good</th>}
            {qualityTab !== "GOOD" && <th>Bad</th>}
          </tr>
        </thead>
        <tbody>
          {rangeData.map((row, i) =>
            rangeParts
              .filter((p) => !selectedPart || p.part_number === selectedPart)
              .map((part, idx) => {
                const key = safeKey(part.part_number);
                const good = row[`${key}_good`] || 0;
                const bad = row[`${key}_bad`] || 0;

                if (
                  (qualityTab === "GOOD" && good === 0) ||
                  (qualityTab === "BAD" && bad === 0)
                ) {
                  return null;
                }

                return (
                  <tr key={`${i}-${idx}`}>
                    <td>{row.date}</td>
                    <td>{part.part_number}</td>
                    {qualityTab !== "BAD" && <td>{good}</td>}
                    {qualityTab !== "GOOD" && <td>{bad}</td>}
                  </tr>
                );
              })
          )}
        </tbody>
      </Table>
    </>
  );
};

export default DateRangeReport;
